import type { MatchResult, MatchState, Team } from '../game/types';
import { SIM_DT } from '../game/config';
import { t } from '../i18n';
import { localizedRoomErrorMessage } from '../i18n/presentation';
import { MenuGamepadInput } from '../game/input/MenuGamepadInput';
import type { RoomClient } from './RoomClient';
import type { RoomDescription, RoomParticipant, RoomSlot } from './roomProtocol';
import './online.css';

const teamLabel = (team: Team): string => (
  team === 'blue' ? t('online:matchHud.teams.blue') : t('online:matchHud.teams.orange')
);

export function formatMatchClock(state: MatchState): string {
  if (state.clock.overtime) {
    const seconds = Math.floor(state.clock.overtimeTicks * SIM_DT);
    return `+${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }
  if (state.clock.remainingSeconds === null) return '∞';
  const seconds = Math.max(0, Math.ceil(state.clock.remainingSeconds));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export class OnlineMatchHud {
  readonly #room: RoomClient;
  readonly #root: HTMLElement;
  readonly #clock: HTMLElement;
  readonly #preparation: HTMLElement;
  readonly #slots: HTMLElement;
  readonly #actions: HTMLElement;
  readonly #replacement: HTMLElement;
  readonly #status: HTMLElement;
  readonly #result: HTMLDialogElement;
  readonly #resultTitle: HTMLElement;
  readonly #resultReason: HTMLElement;
  readonly #onExit: () => void;
  readonly #onMenuStateChange: () => void;
  readonly #unsubscribe: () => void;
  readonly #timer: ReturnType<typeof setInterval>;
  #description: RoomDescription;
  #matchState: MatchState | null = null;
  #replacementSignature = '';
  #statusMessage = '';
  #connectionInterrupted = false;
  #forfeitVote: { readonly expiresAt: number; readonly voterParticipantIds: readonly string[] } | null = null;
  #resultFocused = false;
  readonly #resultGamepad = new MenuGamepadInput();
  #resultFrame = 0;

  constructor(screen: HTMLElement, room: RoomClient, onExit: () => void, onFindAgain: () => void,
    onMenuStateChange: () => void = () => undefined) {
    this.#room = room;
    this.#onExit = onExit;
    this.#onMenuStateChange = onMenuStateChange;
    this.#description = room.welcome.room;
    this.#root = document.createElement('div');
    this.#root.className = 'online-match-hud';
    this.#root.innerHTML = `
      <section class="online-preparation" aria-label="${t('online:matchHud.preparationAria')}" hidden>
        <header><span>${t('online:matchHud.onlineMatch')}</span><strong>${t('online:matchHud.preparingPlayers')}</strong></header>
        <div class="online-preparation__slots"></div>
      </section>
      <section class="online-match-actions" hidden>
        <div class="online-match-actions__replacement"></div>
        <p class="online-match-actions__status" role="status" hidden></p>
      </section>
      <dialog class="online-match-result" aria-label="${t('online:matchHud.resultAria')}" hidden>
        <div class="online-match-result__card">
          <span>${t('online:matchHud.matchEnded')}</span><h2></h2><p></p>
          <div><button type="button" data-action="again">${t('online:matchHud.findAgain')}</button>
            <button type="button" data-action="exit">${t('online:matchHud.back')}</button></div>
        </div>
      </dialog>
      `;
    this.#clock = document.createElement('strong');
    this.#clock.className = 'online-match-clock';
    this.#clock.setAttribute('aria-label', t('online:matchHud.clockAria'));
    const scoreboard = screen.querySelector('.scoreboard');
    scoreboard?.insertAdjacentElement('afterend', this.#clock);
    this.#preparation = this.#root.querySelector('.online-preparation')!;
    this.#slots = this.#root.querySelector('.online-preparation__slots')!;
    this.#actions = this.#root.querySelector('.online-match-actions')!;
    this.#replacement = this.#root.querySelector('.online-match-actions__replacement')!;
    this.#status = this.#root.querySelector('.online-match-actions__status')!;
    this.#result = this.#root.querySelector('.online-match-result')!;
    this.#result.addEventListener('cancel', event => event.preventDefault());
    this.#resultTitle = this.#root.querySelector('.online-match-result h2')!;
    this.#resultReason = this.#root.querySelector('.online-match-result p')!;
    this.#root.querySelector('[data-action="exit"]')?.addEventListener('click', () => this.requestExit());
    this.#root.querySelector('[data-action="again"]')?.addEventListener('click', onFindAgain);
    screen.append(this.#root);
    this.#unsubscribe = room.subscribe({ onEvent: (event) => {
      if (event.type === 'welcome' || event.type === 'roster') {
        this.#description = event.room;
        this.#render();
        this.#onMenuStateChange();
      } else if (event.type === 'forfeit-votes') {
        const localTeam = this.#description.participants.find((item) => item.id === this.#room.welcome.participantId)?.team;
        if (event.team !== localTeam) return;
        this.#forfeitVote = event.expiresAt > Date.now()
          ? { expiresAt: event.expiresAt, voterParticipantIds: event.voterParticipantIds } : null;
        if (event.expiresAt === 0) {
          this.#showStatus(t('online:matchHud.status.voteClosed'));
        } else {
          this.#showStatus(event.pendingNames.length
            ? t('online:matchHud.status.waitingForPlayerVote', { names: event.pendingNames.join(', ') })
            : t('online:matchHud.status.voteInProgress'));
        }
        this.#renderActions();
        this.#onMenuStateChange();
      } else if (event.type === 'error' && !event.fatal) {
        this.#showStatus(localizedRoomErrorMessage(event.code));
        this.#render();
      }
    }, onConnection: (connected) => {
      this.#connectionInterrupted = !connected;
      this.#renderStatus();
      this.#render();
      this.#onMenuStateChange();
    } });
    this.#timer = setInterval(() => this.#renderActions(), 500);
    this.#render();
  }

  updateMatchState(state: MatchState): void {
    this.#matchState = state;
    this.#clock.textContent = formatMatchClock(state);
    this.#clock.dataset.overtime = String(state.clock.overtime);
    this.#renderResult();
    this.#renderActions();
  }

  get forfeitAvailable(): boolean {
    const description = this.#description;
    const participant = description.participants.find(item => item.id === this.#room.welcome.participantId);
    return description.phase === 'running' && this.#matchState?.result === null && participant?.role === 'player'
      && (this.#matchState?.clock.playedTicks ?? 0) * SIM_DT >= description.forfeitAfterPlayedSeconds;
  }

  get hasVotedForfeit(): boolean {
    return Boolean(this.#forfeitVote && this.#forfeitVote.expiresAt > Date.now()
      && this.#forfeitVote.voterParticipantIds.includes(this.#room.welcome.participantId));
  }

  canForfeit(): boolean {
    return this.forfeitAvailable && !this.hasVotedForfeit && this.#room.connected;
  }

  requestForfeit(): boolean {
    if (!this.canForfeit() || !this.#room.sendControl({ type: 'forfeit-vote' })) return false;
    this.#showStatus(t('online:matchHud.status.sendingForfeitVote'));
    return true;
  }

  requestExit(): void {
    this.#onExit();
  }

  dispose(): void {
    if (this.#resultFrame) cancelAnimationFrame(this.#resultFrame);
    this.#resultFrame = 0;
    if (this.#result.open) this.#result.close();
    this.#result.hidden = true;
    clearInterval(this.#timer);
    this.#unsubscribe();
    this.#clock.remove();
    this.#root.remove();
  }

  #render(): void {
    const description = this.#description;
    this.#preparation.hidden = description.phase !== 'preparing';
    if (!this.#preparation.hidden) {
      const participants = new Map(description.participants.map((participant) => [participant.id, participant]));
      const fragment = document.createDocumentFragment();
      for (const team of ['blue', 'orange'] as const) {
        const section = document.createElement('section');
        section.className = 'online-preparation__team';
        section.dataset.team = team;
        const heading = document.createElement('h3');
        heading.textContent = teamHeading(team);
        section.append(heading);
        for (const slot of description.slots.filter((candidate) => candidate.team === team)) {
          section.append(this.#createSlot(slot, participants.get(slot.occupantId ?? '')));
        }
        fragment.append(section);
      }
      this.#slots.replaceChildren(fragment);
    }
    this.#renderActions();
    this.#renderResult();
  }

  #createSlot(slot: RoomSlot, participant: RoomParticipant | undefined): HTMLElement {
    const row = document.createElement('div');
    row.className = 'online-preparation__slot';
    row.dataset.status = slot.status;
    const name = document.createElement('strong');
    name.textContent = participant?.displayName ?? t('online:matchHud.emptySlot', {
      slot: slot.id.split('-').at(-1) ?? '',
    });
    const status = document.createElement('span');
    status.textContent = slotStatusLabel(slot.status);
    row.append(name, status);
    return row;
  }

  #renderActions(): void {
    if (this.#forfeitVote && this.#forfeitVote.expiresAt <= Date.now()) {
      this.#forfeitVote = null;
      this.#onMenuStateChange();
    }
    const description = this.#description;
    const participant = description.participants.find((item) => item.id === this.#room.welcome.participantId);
    const running = description.phase === 'running' && this.#matchState?.result === null;
    const eligibleSlots = running && participant?.role === 'spectator' && description.settings.mode === 'casual'
      ? description.slots.filter((slot) => description.participants.some((candidate) =>
        candidate.id === slot.occupantId && candidate.botId === 'sparring')) : [];
    const replacementSignature = eligibleSlots.map((slot) => `${slot.id}:${slot.occupantId}`).join('|');
    if (replacementSignature !== this.#replacementSignature) {
      this.#replacementSignature = replacementSignature;
      this.#replacement.replaceChildren();
      for (const slot of eligibleSlots) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = playOnTeamLabel(slot.team);
        button.addEventListener('click', () => {
          if (this.#room.sendControl({ type: 'request-replacement', slotId: slot.id })) {
            button.disabled = true;
            this.#showStatus(t('online:matchHud.status.preparingEntry'));
          }
        });
        this.#replacement.append(button);
      }
    }
    this.#actions.hidden = !this.#replacement.childElementCount && this.#status.hidden;
  }

  #renderResult(): void {
    const result = this.#matchState?.result ?? this.#description.result;
    const visible = this.#description.phase === 'finished' || result !== null;
    if (!visible) {
      if (this.#resultFrame) cancelAnimationFrame(this.#resultFrame);
      this.#resultFrame = 0;
      if (this.#result.open) this.#result.close();
      this.#result.hidden = true;
      this.#resultFocused = false;
      return;
    }
    this.#result.hidden = false;
    if (!this.#result.open) this.#result.showModal();
    if (!this.#resultFocused) {
      this.#resultFocused = true;
      this.#resultGamepad.reset();
      this.#resultFrame = requestAnimationFrame(this.#pollResultGamepad);
      queueMicrotask(() => {
        if (this.#result.open) this.#root.querySelector<HTMLButtonElement>('[data-action="exit"]')?.focus();
      });
    }
    const again = this.#root.querySelector<HTMLButtonElement>('[data-action="again"]');
    if (again) again.disabled = this.#description.resultConfirmation !== 'confirmed';
    if (this.#description.resultConfirmation !== 'confirmed') {
      this.#resultTitle.textContent = t('online:matchHud.result.confirmingTitle');
      this.#resultReason.textContent = t('online:matchHud.result.confirmingReason');
      return;
    }
    if (result === null) {
      this.#resultTitle.textContent = t('online:matchHud.result.confirmingTitle');
      this.#resultReason.textContent = t('online:matchHud.result.confirmingReason');
      return;
    }
    const participant = this.#description.participants.find((item) => item.id === this.#room.welcome.participantId);
    this.#resultTitle.textContent = resultTitle(result, participant?.team ?? null);
    this.#resultReason.textContent = resultReason(result);
  }

  #showStatus(message: string): void {
    this.#statusMessage = message;
    this.#renderStatus();
  }

  #renderStatus(): void {
    const message = this.#connectionInterrupted ? t('online:matchHud.status.reconnecting') : this.#statusMessage;
    this.#status.textContent = message;
    this.#status.hidden = !message;
    this.#actions.hidden = !this.#replacement.childElementCount && this.#status.hidden;
  }

  #pollResultGamepad = (): void => {
    if (!this.#result.open) { this.#resultFrame = 0; return; }
    const action = this.#resultGamepad.poll();
    if (action === 'back') this.#root.querySelector<HTMLButtonElement>('[data-action="exit"]')?.click();
    else if (action === 'activate') (document.activeElement as HTMLElement | null)?.click();
    else if (action === 'previous' || action === 'next') {
      const buttons = [...this.#result.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(index + (action === 'next' ? 1 : -1) + buttons.length) % buttons.length]?.focus({ preventScroll: true });
    }
    if (this.#result.open) this.#resultFrame = requestAnimationFrame(this.#pollResultGamepad);
  };
}

function resultTitle(result: MatchResult, localTeam: Team | null): string {
  if (result.reason === 'cancelled') return t('online:matchHud.result.cancelled');
  if (result.winner === null) return t('online:matchHud.result.draw');
  if (localTeam === null) return t('online:matchHud.result.teamVictory', { team: teamLabel(result.winner) });
  return localTeam === result.winner
    ? t('online:matchHud.result.victory')
    : t('online:matchHud.result.defeat');
}

function resultReason(result: MatchResult): string {
  const reason = resultReasonLabel(result.reason);
  return t('online:matchHud.result.summary', {
    blue: result.score.blue,
    orange: result.score.orange,
    reason,
  });
}

function slotStatusLabel(status: RoomSlot['status']): string {
  switch (status) {
    case 'searching': return t('online:matchHud.slotStatus.searching');
    case 'vacant': return t('online:matchHud.slotStatus.vacant');
    case 'entering': return t('online:matchHud.slotStatus.entering');
    case 'loaded': return t('online:matchHud.slotStatus.ready');
    case 'reconnecting': return t('online:matchHud.slotStatus.reconnecting');
  }
}

function teamHeading(team: Team): string {
  return team === 'blue'
    ? t('online:matchHud.teamHeading.blue')
    : t('online:matchHud.teamHeading.orange');
}

function playOnTeamLabel(team: Team): string {
  return team === 'blue'
    ? t('online:matchHud.playOnTeam.blue')
    : t('online:matchHud.playOnTeam.orange');
}

function resultReasonLabel(reason: MatchResult['reason']): string {
  switch (reason) {
    case 'time': return t('online:matchHud.result.reason.time');
    case 'golden-goal': return t('online:matchHud.result.reason.goldenGoal');
    case 'forfeit': return t('online:matchHud.result.reason.forfeit');
    case 'abandonment': return t('online:matchHud.result.reason.abandonment');
    case 'cancelled': return t('online:matchHud.result.reason.cancelled');
  }
}
